import { defineTable } from "convex/server";
import { type Infer, v } from "convex/values";
import { literalsValidator } from "@convex/validators/literals";

/**
 * Brand kits (Dylan, 2026-10-23): named sets of the platform's own images — DisputeWell's logo for
 * light and dark backgrounds, its tab icon, and Fluid's mark for light and dark mode. Every user of
 * the deployment sees every kit, and anyone signed in to the CRM can add one; which kit a browser
 * wears is its user's own choice (the dw-brand-kit cookie). The images the platform ships are the
 * Default kit, which is not a row: an empty slot in a kit shows the Default kit's image.
 */

/** The images a kit holds, in the order the setting lists them. */
export const BRAND_IMAGE_SLOTS = ["disputewellLogo", "disputewellLogoDark", "disputewellIcon", "fluidMark", "fluidMarkDark"] as const;
export type BrandImageSlot = (typeof BRAND_IMAGE_SLOTS)[number];
export const brandImageSlotValidator = literalsValidator(BRAND_IMAGE_SLOTS);

/** The types a kit image may be: the setting's file picker offers them, and a kit refuses any other. */
export const BRAND_IMAGE_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp", "image/gif", "image/x-icon", "image/vnd.microsoft.icon"] as const;
export type BrandImageType = (typeof BRAND_IMAGE_TYPES)[number];
export const MAX_BRAND_IMAGE_BYTES = 5 * 1024 * 1024;

export function isBrandImageType(contentType: string | undefined): contentType is BrandImageType {
    return BRAND_IMAGE_TYPES.some((type) => type === contentType);
}

/** An image in a kit's slot: the stored file, and the name it was uploaded under. */
export const brandKitImageValidator = v.object({ storageId: v.id("_storage"), fileName: v.string() });
export type BrandKitImage = Infer<typeof brandKitImageValidator>;

const slotImageValidator = v.union(brandKitImageValidator, v.null());
/** Each slot holds an image or nothing. An empty slot shows the Default kit's image; an empty dark
 *  slot shows the kit's own light image first. */
export const brandKitSlotsValidator = v.object({
    disputewellLogo: slotImageValidator,
    disputewellLogoDark: slotImageValidator,
    disputewellIcon: slotImageValidator,
    fluidMark: slotImageValidator,
    fluidMarkDark: slotImageValidator,
} satisfies Record<BrandImageSlot, typeof slotImageValidator>);
export type BrandKitSlots = Infer<typeof brandKitSlotsValidator>;

export const EMPTY_BRAND_KIT_SLOTS: BrandKitSlots = { disputewellLogo: null, disputewellLogoDark: null, disputewellIcon: null, fluidMark: null, fluidMarkDark: null };

export const brandKitValidator = v.object({
    name: v.string(),
    images: brandKitSlotsValidator,
    /** Who made the kit (their Better Auth user id, and the name the setting shows): they and a
     *  platform admin may change it or delete it. */
    createdByUserId: v.string(),
    createdByName: v.string(),
    createdAt: v.number(),
    updatedAt: v.number(),
});

export const brandKits = defineTable(brandKitValidator).index("by_createdAt", ["createdAt"]);
